import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FaCircleCheck } from 'react-icons/fa6';
import { CgClose } from 'react-icons/cg';

import { Palette } from './utils';
import { Flex } from './ui/Flex';
import ImageIcon from './icons/ImageIcon';
import PlusIcon from './icons/PlusIcon';
import { ACCEPTED_IMG_EXTENSIONS } from '../views/Deliveries/constants';
import { MediaKeys } from '../views/offers/utils';
import { useImageUploadWithPreview } from '../hooks/useImageUploadWithPreview';
import { FormField, FormMessage } from './ui/Form';
import { formatMbFileSize } from '../utils/utils';

const ImageUploadWithPreviewV3 = ({
  form,
  existingImageUrl,
  fieldName,
  instructionText,
  altText,
  deleteButtonText,
  constraintsText,
  onDeleteExistingImage
}) => {
  const { t } = useTranslation();
  const {
    getRootProps,
    getInputProps,
    preview,
    removeImage,
    shouldDeleteExistingImage,
    handleDeleteExistingImage
  } = useImageUploadWithPreview({
    form,
    fieldName,
    acceptedImageFormats: ACCEPTED_IMG_EXTENSIONS,
    mediaKey: MediaKeys.MEDIA
  });

  const handleDeleteExisting = async () => {
    await handleDeleteExistingImage();
    if (onDeleteExistingImage) {
      onDeleteExistingImage();
    }
  };

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={(controllerProps) => (
        <>
          <ImageDropzoneContainer {...getRootProps()} {...controllerProps}>
            <input {...getInputProps()} />

            <ImageUploadContainer gapY="24px" alignItems="center">
              <CircleContainer $hasImage={!!preview || (!!existingImageUrl && !shouldDeleteExistingImage)}>
                {preview ? (
                  <PreviewImage src={preview.previewURL} alt={preview.fileName} />
                ) : existingImageUrl && !shouldDeleteExistingImage ? (
                  <PreviewImage src={existingImageUrl} alt={altText} />
                ) : (
                  <ImageIconContainer>
                    <ImageIcon />
                    <PlusIconWrapper>
                      <PlusIcon />
                    </PlusIconWrapper>
                  </ImageIconContainer>
                )}
              </CircleContainer>
              <div>
                <ImageUploadInstructionText>{instructionText}</ImageUploadInstructionText>
                <ImageConstraintsText>
                  {constraintsText || t('5MB max file size. JPG and PNG supported.')}
                </ImageConstraintsText>
              </div>
            </ImageUploadContainer>
          </ImageDropzoneContainer>
          {existingImageUrl && !shouldDeleteExistingImage && !preview && (
            <DeleteAvatarButton type="button" onClick={handleDeleteExisting}>
              {deleteButtonText || t('Delete Avatar')}
            </DeleteAvatarButton>
          )}
          <FormMessage />
          {preview && (
            <PreviewInfo justify="space-between" alignItems="center" gapY="1rem">
              <Flex gapY="0.625rem" alignItems="center">
                <PreviewInfoImageIcon />
                <div>
                  <p>{preview.fileName}</p>
                  <PreviewImageSize>{formatMbFileSize(preview.image.size)}MB</PreviewImageSize>
                </div>
              </Flex>
              <Flex gapY="1.1875rem" alignItems="center">
                <p>100%</p>
                <CheckIcon />
                <ClearPreviewButton onClick={removeImage}>
                  <CgClose />
                </ClearPreviewButton>
              </Flex>
            </PreviewInfo>
          )}
        </>
      )}
    />
  );
};

export default ImageUploadWithPreviewV3;

export const ImageDropzoneContainer = styled.div`
  margin-top: 8px;
  border: 1px dashed ${Palette.OCEANIC};
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
`;

export const PreviewImage = styled.img`
  width: 100%;
  object-fit: cover;
`;

export const ImageUploadContainer = styled(Flex)`
  padding: 24px;
`;

export const CircleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  padding: ${(props) => (props.$hasImage ? '3px' : '0')};
  background-color: ${(props) => (props.$hasImage ? Palette.GHOST_WHITE : Palette.SOLITUDE)};
  border-radius: 50%;
  border: ${(props) => (props.$hasImage ? `0.5px solid #D4D4D4` : 'none')};
  overflow: hidden;
`;

export const ImageIconContainer = styled.div`
  position: relative;
`;

export const PlusIconWrapper = styled.div`
  position: absolute;
  width: 27px;
  height: 27px;
  background-color: #fff;
  border-radius: 50%;
  bottom: -3px;
  right: -6px;

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const PreviewInfo = styled(Flex)`
  padding: 0.625rem 1rem;
  border-radius: 10px;
  border: 1px solid ${Palette.STROKE_GREY};
  font-weight: 600;
  color: ${Palette.BLACK};

  p {
    margin-bottom: 0;
  }
`;

const PreviewInfoImageIcon = styled(ImageIcon)`
  width: 2.75rem;
  height: 2.75rem;
`;

const PreviewImageSize = styled.p`
  font-size: 0.75rem;
  color: ${Palette.BLACK_50};
`;

const CheckIcon = styled(FaCircleCheck)`
  width: 1.5rem;
  height: 1.5rem;
  fill: ${Palette.ACTIVE_GREEN};
`;

const ClearPreviewButton = styled.button`
  display: flex;
  padding: 0;
  border: none;
  background-color: transparent;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    fill: ${Palette.BLACK};
  }
`;

export const ImageUploadInstructionText = styled.p`
  font-size: 14px;
  color: ${Palette.BLACK};
  margin-bottom: 13px;
`;

export const ImageConstraintsText = styled.p`
  font-size: 12px;
  color: ${Palette.OCEANIC};
  margin-bottom: 0;
`;

export const DeleteAvatarButton = styled.button`
  margin-left: auto;
  background-color: transparent;
  border: none;
  font-weight: 400;
  font-size: 12px;
  color: ${Palette.CONTENT_TERTIARY};
  cursor: pointer;

  :focus {
    outline: none;
  }

  :focus-visible {
    outline: 1px solid ${Palette.OCEANIC};
  }
`;
